import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CriticalSystem, CriticalSystemType, CriticalSystemTypes } from '@/types/CriticalInfo.ts';
import {
	createEmptySystem,
	DEFAULT_CRITICAL_SYSTEMS,
	documentToForm,
	formToDocument,
} from '@/utils/criticalInfoUtils.ts';
import { useCriticalInfo } from '@/hooks/house/critical-info/useCriticalInfo.ts';
import { useCreateCriticalInfo } from '@/hooks/house/critical-info/mutations/createCriticalInfo.ts';
import { useUpdateCriticalInfo } from '@/hooks/house/critical-info/mutations/updateCriticalInfo.ts';
import { houseDocumentKeys } from '@/hooks/house/documents/queries/queryKeys.ts';
import { makeLogger } from '@/utils/logger/logger.ts';

const logger = makeLogger('useCriticalInfoDocument');

/**
 * Hook for managing critical house information documents.
 * Provides functionality to load, save, and manipulate important information
 * about house systems that are essential in emergency situations.
 */
export const useCriticalInfoDocument = (houseId: number) => {
	const queryClient = useQueryClient();
	const [systems, setSystems] = useState<CriticalSystem[]>(
		DEFAULT_CRITICAL_SYSTEMS.map((system) => createEmptySystem(system.name, CriticalSystemTypes.Critical)),
	);

	const { data: criticalInfoData, isLoading, error, exists: hasCriticalInfo, refetch } = useCriticalInfo(houseId);

	useEffect(() => {
		if (criticalInfoData) {
			try {
				const formData = documentToForm(criticalInfoData);
				setSystems(formData);
			} catch (err) {
				logger.error('Error processing critical info document:', err);
			}
		}
	}, [criticalInfoData]);

	// Use the mutation hooks
	const { mutateAsync: createCriticalInfo, isPending: isCreating } = useCreateCriticalInfo(houseId);
	const { mutateAsync: updateCriticalInfo, isPending: isUpdating } = useUpdateCriticalInfo(houseId);

	const saveCriticalInfo = async (systems: CriticalSystem[]) => {
		const documentData = formToDocument(systems);

		logger.info('Saving critical info', {
			houseId,
			hasCriticalInfo,
			systemsCount: systems.length,
		});

		try {
			if (hasCriticalInfo) {
				logger.info('Updating existing critical info document', { houseId });
				await updateCriticalInfo({
					// @ts-expect-error generated mutation keeps requiring path even though hook options provide it
					body: documentData,
				});
			} else {
				logger.info('Creating new critical info document', { houseId });
				await createCriticalInfo(documentData);
			}
			await Promise.all([
				refetch(),
				queryClient.invalidateQueries({ queryKey: houseDocumentKeys.list(houseId) }),
			]);
		} catch (error) {
			logger.error('Error saving critical info document:', error);
			throw error;
		}
	};

	const isSaving = isCreating || isUpdating;

	const addSystem = (name: string, type?: CriticalSystemType) => {
		setSystems((prev) => [...prev, createEmptySystem(name, type)]);
	};

	const removeSystem = (name: string) => {
		setSystems((prev) => prev.filter((system) => system.name !== name));
	};

	const replaceSystems = (nextSystems: CriticalSystem[]) => {
		setSystems(nextSystems);
	};

	const updateSystemField = (systemName: string, fieldName: string, value: string) => {
		setSystems((prev) =>
			prev.map((system) =>
				system.name === systemName
					? {
							...system,
							fields: system.fields.map((field) =>
								field.name === fieldName ? { ...field, value } : field,
							),
						}
					: system,
			),
		);
	};

	return {
		systems,
		isLoading,
		error,
		isSaving,
		hasCriticalInfo,
		addSystem,
		removeSystem,
		replaceSystems,
		updateSystemField,
		saveCriticalInfo,
		refetch,
		criticalInfoData,
	};
};
